import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import OrderRoutingRulesService from './OrderRoutingRulesService';
import OrderByOptionService from '../orderByOption/OrderByOptionService';
import axios from 'axios';

const BLANK_ORDERER_RULE = {
  orderByOptionId: '',
  website: '',
  amountMin: '',
  amountMax: '',
  paymentMethod: '',
  priority: 100,
  isActive: true,
  suggestedWarehouse: '',
};

const BLANK_WAREHOUSE_RULE = {
  warehouseReceivingAddressId: '',
  website: '',
  isAir: '',
  isCardCustomer: '',
  priority: 100,
  isActive: true,
};

const IS_CARD_OPTIONS = [
  { value: '', label: 'Any (ลูกค้าทุกกลุ่ม)' },
  { value: 'true', label: 'การ์ดเท่านั้น (productGroup = การ์ด)' },
  { value: 'false', label: 'ไม่ใช่การ์ด' },
];

const WEBSITE_OPTIONS = [
  { value: '', label: 'Any (ไม่ระบุ)' },
  { value: 'Mercari', label: 'Mercari' },
  { value: 'PayPay', label: 'PayPay Flea Market' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'ไม่ระบุ (Any)' },
  { value: 'sevenEleven', label: 'Seven-Eleven' },
];

const IS_AIR_OPTIONS = [
  { value: '', label: 'Any (ไม่ระบุ)' },
  { value: 'true', label: 'Air / Hand Carry' },
  { value: 'false', label: 'Sea / Ship' },
];

const OrderRoutingRulesSetting = () => {
  const [tab, setTab] = useState(0);

  // Orderer rules state
  const [ordererRules, setOrdererRules] = useState([]);
  const [orderByOptions, setOrderByOptions] = useState([]);
  const [ordererDialog, setOrdererDialog] = useState(false);
  const [ordererForm, setOrdererForm] = useState(BLANK_ORDERER_RULE);
  const [editingOrdererRuleId, setEditingOrdererRuleId] = useState(null);
  const [deletingOrdererRuleId, setDeletingOrdererRuleId] = useState(null);

  // Warehouse rules state
  const [warehouseRules, setWarehouseRules] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseDialog, setWarehouseDialog] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState(BLANK_WAREHOUSE_RULE);
  const [editingWarehouseRuleId, setEditingWarehouseRuleId] = useState(null);
  const [deletingWarehouseRuleId, setDeletingWarehouseRuleId] = useState(null);

  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [oRules, wRules, oOptions, wOptions] = await Promise.all([
        OrderRoutingRulesService.getOrdererRules(),
        OrderRoutingRulesService.getWarehouseRules(),
        OrderByOptionService.getOrderByOptions(),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/setting/warehouse-receiving-address`),
      ]);
      setOrdererRules(oRules.data || []);
      setWarehouseRules(wRules.data || []);
      setOrderByOptions(oOptions.data || []);
      setWarehouseOptions(wOptions.data || []);
    } catch (err) {
      toast.error('โหลดข้อมูลล้มเหลว');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // ── Orderer Rules ──────────────────────────────────────────────────────────
  const openCreateOrdererDialog = () => {
    setEditingOrdererRuleId(null);
    setOrdererForm(BLANK_ORDERER_RULE);
    setOrdererDialog(true);
  };

  const openEditOrdererDialog = (rule) => {
    setEditingOrdererRuleId(rule.id);
    setOrdererForm({
      orderByOptionId: rule.orderByOptionId ?? '',
      website: rule.website ?? '',
      amountMin: rule.amountMin ?? '',
      amountMax: rule.amountMax ?? '',
      paymentMethod: rule.paymentMethod ?? '',
      priority: rule.priority ?? 100,
      isActive: rule.isActive ?? true,
      suggestedWarehouse: rule.suggestedWarehouse ?? '',
    });
    setOrdererDialog(true);
  };

  const handleOrdererSave = async () => {
    if (!ordererForm.orderByOptionId) {
      toast.warning('กรุณาเลือก Orderer');
      return;
    }
    const payload = {
      orderByOptionId: parseInt(ordererForm.orderByOptionId, 10),
      website: ordererForm.website || null,
      amountMin: ordererForm.amountMin !== '' ? parseFloat(ordererForm.amountMin) : null,
      amountMax: ordererForm.amountMax !== '' ? parseFloat(ordererForm.amountMax) : null,
      paymentMethod: ordererForm.paymentMethod || null,
      priority: parseInt(ordererForm.priority, 10) || 100,
      isActive: ordererForm.isActive,
      suggestedWarehouse: ordererForm.suggestedWarehouse || null,
    };
    try {
      if (editingOrdererRuleId) {
        await OrderRoutingRulesService.updateOrdererRule(editingOrdererRuleId, payload);
        toast.success('อัพเดทสำเร็จ');
      } else {
        await OrderRoutingRulesService.createOrdererRule(payload);
        toast.success('เพิ่มสำเร็จ');
      }
      setOrdererDialog(false);
      loadAll();
    } catch {
      toast.error('บันทึกล้มเหลว');
    }
  };

  const handleDeleteOrdererRule = async () => {
    try {
      await OrderRoutingRulesService.deleteOrdererRule(deletingOrdererRuleId);
      toast.success('ลบสำเร็จ');
      setDeletingOrdererRuleId(null);
      loadAll();
    } catch {
      toast.error('ลบล้มเหลว');
    }
  };

  const handleToggleOrdererActive = async (rule) => {
    try {
      await OrderRoutingRulesService.updateOrdererRule(rule.id, { isActive: !rule.isActive });
      loadAll();
    } catch {
      toast.error('อัพเดทล้มเหลว');
    }
  };

  // ── Warehouse Rules ────────────────────────────────────────────────────────
  const openCreateWarehouseDialog = () => {
    setEditingWarehouseRuleId(null);
    setWarehouseForm(BLANK_WAREHOUSE_RULE);
    setWarehouseDialog(true);
  };

  const openEditWarehouseDialog = (rule) => {
    setEditingWarehouseRuleId(rule.id);
    setWarehouseForm({
      warehouseReceivingAddressId: rule.warehouseReceivingAddressId ?? '',
      website: rule.website ?? '',
      isAir: rule.isAir != null ? String(rule.isAir) : '',
      isCardCustomer: rule.isCardCustomer != null ? String(rule.isCardCustomer) : '',
      priority: rule.priority ?? 100,
      isActive: rule.isActive ?? true,
    });
    setWarehouseDialog(true);
  };

  const handleWarehouseSave = async () => {
    if (!warehouseForm.warehouseReceivingAddressId) {
      toast.warning('กรุณาเลือกโกดัง');
      return;
    }
    const payload = {
      warehouseReceivingAddressId: parseInt(warehouseForm.warehouseReceivingAddressId, 10),
      website: warehouseForm.website || null,
      isAir: warehouseForm.isAir !== '' ? warehouseForm.isAir === 'true' : null,
      isCardCustomer: warehouseForm.isCardCustomer !== '' ? warehouseForm.isCardCustomer === 'true' : null,
      priority: parseInt(warehouseForm.priority, 10) || 100,
      isActive: warehouseForm.isActive,
    };
    try {
      if (editingWarehouseRuleId) {
        await OrderRoutingRulesService.updateWarehouseRule(editingWarehouseRuleId, payload);
        toast.success('อัพเดทสำเร็จ');
      } else {
        await OrderRoutingRulesService.createWarehouseRule(payload);
        toast.success('เพิ่มสำเร็จ');
      }
      setWarehouseDialog(false);
      loadAll();
    } catch {
      toast.error('บันทึกล้มเหลว');
    }
  };

  const handleDeleteWarehouseRule = async () => {
    try {
      await OrderRoutingRulesService.deleteWarehouseRule(deletingWarehouseRuleId);
      toast.success('ลบสำเร็จ');
      setDeletingWarehouseRuleId(null);
      loadAll();
    } catch {
      toast.error('ลบล้มเหลว');
    }
  };

  const handleToggleWarehouseActive = async (rule) => {
    try {
      await OrderRoutingRulesService.updateWarehouseRule(rule.id, { isActive: !rule.isActive });
      loadAll();
    } catch {
      toast.error('อัพเดทล้มเหลว');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Order Routing Rules
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        กำหนดเงื่อนไขการเลือกผู้สั่งและโกดังอัตโนมัติ
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Orderer Rules (ผู้สั่ง)" />
        <Tab label="Warehouse Rules (โกดัง)" />
      </Tabs>

      {/* ── Orderer Rules Tab ─────────────────────────────────────────────── */}
      {tab === 0 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              startIcon={<PlusIcon className="w-4 h-4" />}
              onClick={openCreateOrdererDialog}
            >
              เพิ่มกฎ
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>Orderer</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Amount Range (JPY)</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Warehouse Filter</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordererRules.map((rule) => (
                  <TableRow key={rule.id} sx={{ opacity: rule.isActive ? 1 : 0.5 }}>
                    <TableCell>{rule.priority}</TableCell>
                    <TableCell>
                      <strong>{rule.orderByOption?.label || `ID: ${rule.orderByOptionId}`}</strong>
                    </TableCell>
                    <TableCell>{rule.website || <em style={{ color: '#999' }}>Any</em>}</TableCell>
                    <TableCell>
                      {rule.amountMin != null || rule.amountMax != null ? (
                        <>
                          {rule.amountMin != null ? `¥${rule.amountMin.toLocaleString()}` : '0'}
                          {' – '}
                          {rule.amountMax != null ? `¥${rule.amountMax.toLocaleString()}` : '∞'}
                        </>
                      ) : (
                        <em style={{ color: '#999' }}>Any</em>
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.paymentMethod ? (
                        <Chip label={rule.paymentMethod} size="small" />
                      ) : (
                        <em style={{ color: '#999' }}>Any</em>
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.suggestedWarehouse ? (
                        <Chip label={rule.suggestedWarehouse} size="small" color="info" />
                      ) : (
                        <em style={{ color: '#999' }}>Any</em>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.isActive}
                        size="small"
                        onChange={() => handleToggleOrdererActive(rule)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="แก้ไข">
                        <IconButton size="small" onClick={() => openEditOrdererDialog(rule)}>
                          <PencilIcon className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ลบ">
                        <IconButton size="small" color="error" onClick={() => setDeletingOrdererRuleId(rule.id)}>
                          <TrashIcon className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {ordererRules.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <em>ยังไม่มีกฎ — กดเพิ่มกฎด้านบน</em>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Warehouse Rules Tab ───────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button
              variant="contained"
              startIcon={<PlusIcon className="w-4 h-4" />}
              onClick={openCreateWarehouseDialog}
            >
              เพิ่มกฎ
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Shipment Type</TableCell>
                  <TableCell>Customer Type</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouseRules.map((rule) => (
                  <TableRow key={rule.id} sx={{ opacity: rule.isActive ? 1 : 0.5 }}>
                    <TableCell>{rule.priority}</TableCell>
                    <TableCell>
                      <strong>{rule.warehouse?.addressNickName || `ID: ${rule.warehouseReceivingAddressId}`}</strong>
                    </TableCell>
                    <TableCell>{rule.website || <em style={{ color: '#999' }}>Any</em>}</TableCell>
                    <TableCell>
                      {rule.isAir == null ? (
                        <em style={{ color: '#999' }}>Any</em>
                      ) : rule.isAir ? (
                        <Chip label="Air" size="small" color="info" />
                      ) : (
                        <Chip label="Sea" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {rule.isCardCustomer == null ? (
                        <em style={{ color: '#999' }}>Any</em>
                      ) : rule.isCardCustomer ? (
                        <Chip label="การ์ด" size="small" color="secondary" />
                      ) : (
                        <Chip label="ไม่ใช่การ์ด" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.isActive}
                        size="small"
                        onChange={() => handleToggleWarehouseActive(rule)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="แก้ไข">
                        <IconButton size="small" onClick={() => openEditWarehouseDialog(rule)}>
                          <PencilIcon className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="ลบ">
                        <IconButton size="small" color="error" onClick={() => setDeletingWarehouseRuleId(rule.id)}>
                          <TrashIcon className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {warehouseRules.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <em>ยังไม่มีกฎ — กดเพิ่มกฎด้านบน</em>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Orderer Rule Dialog ───────────────────────────────────────────── */}
      <Dialog open={ordererDialog} onClose={() => setOrdererDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOrdererRuleId ? 'แก้ไขกฎผู้สั่ง' : 'เพิ่มกฎผู้สั่ง'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Orderer *</InputLabel>
              <Select
                value={ordererForm.orderByOptionId}
                label="Orderer *"
                onChange={(e) => setOrdererForm(p => ({ ...p, orderByOptionId: e.target.value }))}
              >
                {orderByOptions.map(o => (
                  <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Website</InputLabel>
              <Select
                value={ordererForm.website}
                label="Website"
                onChange={(e) => setOrdererForm(p => ({ ...p, website: e.target.value }))}
              >
                {WEBSITE_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" gap={2}>
              <TextField
                label="Amount Min (JPY)"
                size="small"
                type="number"
                value={ordererForm.amountMin}
                onChange={(e) => setOrdererForm(p => ({ ...p, amountMin: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Amount Max (JPY)"
                size="small"
                type="number"
                value={ordererForm.amountMax}
                onChange={(e) => setOrdererForm(p => ({ ...p, amountMax: e.target.value }))}
                fullWidth
              />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={ordererForm.paymentMethod}
                label="Payment Method"
                onChange={(e) => setOrdererForm(p => ({ ...p, paymentMethod: e.target.value }))}
              >
                {PAYMENT_METHOD_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Warehouse Filter (ว่าง = ทุกโกดัง)</InputLabel>
              <Select
                label="Warehouse Filter (ว่าง = ทุกโกดัง)"
                value={ordererForm.suggestedWarehouse}
                onChange={(e) => setOrdererForm(p => ({ ...p, suggestedWarehouse: e.target.value }))}
              >
                <MenuItem value="">Any (ทุกโกดัง)</MenuItem>
                {warehouseOptions.map(w => (
                  <MenuItem key={w.id} value={w.addressNickName}>{w.addressNickName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Priority (ต่ำกว่า = ตรวจก่อน)"
              size="small"
              type="number"
              value={ordererForm.priority}
              onChange={(e) => setOrdererForm(p => ({ ...p, priority: e.target.value }))}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={ordererForm.isActive}
                onChange={(e) => setOrdererForm(p => ({ ...p, isActive: e.target.checked }))}
              />
              <Typography variant="body2">Active</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrdererDialog(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleOrdererSave}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* ── Warehouse Rule Dialog ─────────────────────────────────────────── */}
      <Dialog open={warehouseDialog} onClose={() => setWarehouseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingWarehouseRuleId ? 'แก้ไขกฎโกดัง' : 'เพิ่มกฎโกดัง'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Warehouse *</InputLabel>
              <Select
                value={warehouseForm.warehouseReceivingAddressId}
                label="Warehouse *"
                onChange={(e) => setWarehouseForm(p => ({ ...p, warehouseReceivingAddressId: e.target.value }))}
              >
                {warehouseOptions.map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.addressNickName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Website</InputLabel>
              <Select
                value={warehouseForm.website}
                label="Website"
                onChange={(e) => setWarehouseForm(p => ({ ...p, website: e.target.value }))}
              >
                {WEBSITE_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Shipment Type</InputLabel>
              <Select
                value={warehouseForm.isAir}
                label="Shipment Type"
                onChange={(e) => setWarehouseForm(p => ({ ...p, isAir: e.target.value }))}
              >
                {IS_AIR_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>ประเภทลูกค้า (การ์ด)</InputLabel>
              <Select
                value={warehouseForm.isCardCustomer}
                label="ประเภทลูกค้า (การ์ด)"
                onChange={(e) => setWarehouseForm(p => ({ ...p, isCardCustomer: e.target.value }))}
              >
                {IS_CARD_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Priority (ต่ำกว่า = ตรวจก่อน)"
              size="small"
              type="number"
              value={warehouseForm.priority}
              onChange={(e) => setWarehouseForm(p => ({ ...p, priority: e.target.value }))}
            />
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={warehouseForm.isActive}
                onChange={(e) => setWarehouseForm(p => ({ ...p, isActive: e.target.checked }))}
              />
              <Typography variant="body2">Active</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarehouseDialog(false)}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleWarehouseSave}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm — Orderer ──────────────────────────────────────── */}
      <Dialog open={!!deletingOrdererRuleId} onClose={() => setDeletingOrdererRuleId(null)}>
        <DialogTitle>ยืนยันการลบกฎผู้สั่ง</DialogTitle>
        <DialogContent>
          <Typography>ต้องการลบกฎนี้หรือไม่? การดำเนินการไม่สามารถย้อนกลับได้</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingOrdererRuleId(null)}>ยกเลิก</Button>
          <Button onClick={handleDeleteOrdererRule} color="error" variant="contained">ลบ</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm — Warehouse ────────────────────────────────────── */}
      <Dialog open={!!deletingWarehouseRuleId} onClose={() => setDeletingWarehouseRuleId(null)}>
        <DialogTitle>ยืนยันการลบกฎโกดัง</DialogTitle>
        <DialogContent>
          <Typography>ต้องการลบกฎนี้หรือไม่? การดำเนินการไม่สามารถย้อนกลับได้</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingWarehouseRuleId(null)}>ยกเลิก</Button>
          <Button onClick={handleDeleteWarehouseRule} color="error" variant="contained">ลบ</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderRoutingRulesSetting;
